import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import CompanyAdminDashboard from './pages/CompanyAdminDashboard';
import CompanyManagerDashboard from './pages/CompanyManagerDashboard';
import CompanyEmployeeDashboard from './pages/CompanyEmployeeDashboard';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    switch (user?.role) {
      case 'super_admin': return <Navigate to="/super-admin/dashboard" replace />;
      case 'manager': return <Navigate to="/manager/dashboard" replace />;
      case 'company_admin': return <Navigate to="/company-admin/dashboard" replace />;
      case 'company_manager': return <Navigate to="/company-manager/dashboard" replace />;
      case 'company_employee': return <Navigate to="/company-employee/dashboard" replace />;
      default: return <Navigate to="/login" replace />;
    }
  }
  
  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  switch (user.role) {
    case 'super_admin': return <Navigate to="/super-admin/dashboard" replace />;
    case 'manager': return <Navigate to="/manager/dashboard" replace />;
    case 'company_admin': return <Navigate to="/company-admin/dashboard" replace />;
    case 'company_manager': return <Navigate to="/company-manager/dashboard" replace />;
    case 'company_employee': return <Navigate to="/company-employee/dashboard" replace />;
    default: return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/" element={<DashboardRouter />} />
          
          {/* Super Admin Routes */}
          <Route path="/super-admin/dashboard" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/companies" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/airlines" element={<Navigate to="/super-admin/companies" replace />} />
          <Route path="/super-admin/managers" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/assign-me" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/visa-applications" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/error-pool" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/finalize-pool" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/reassign" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/reports" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/super-admin/currency-minimum-balances" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Manager Routes (My Side) */}
          <Route path="/manager/dashboard" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/assign-me" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/visa-applications" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/error-pool" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/finalize-pool" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/reports" element={
            <ProtectedRoute allowedRoles={['manager']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } />
          
          {/* Company Admin Routes */}
          <Route path="/company-admin/dashboard" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/visa-applications" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/error-pool" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/finalize-pool" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/managers" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/staff" element={<Navigate to="/company-admin/dashboard" replace />} />
          <Route path="/company-admin/profile" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/billing" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/reports" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/api-integration" element={<Navigate to="/company-admin/employees" replace />} />
          <Route path="/company-admin/apply-visa" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-admin/employees" element={
            <ProtectedRoute allowedRoles={['company_admin']}>
              <CompanyAdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Company Manager Routes */}
          <Route path="/company-manager/dashboard" element={
            <ProtectedRoute allowedRoles={['company_manager']}>
              <CompanyManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-manager/visa-applications" element={
            <ProtectedRoute allowedRoles={['company_manager']}>
              <CompanyManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-manager/error-pool" element={
            <ProtectedRoute allowedRoles={['company_manager']}>
              <CompanyManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-manager/finalize-pool" element={
            <ProtectedRoute allowedRoles={['company_manager']}>
              <CompanyManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-manager/reports" element={
            <ProtectedRoute allowedRoles={['company_manager']}>
              <CompanyManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-manager/apply-visa" element={
            <ProtectedRoute allowedRoles={['company_manager']}>
              <CompanyManagerDashboard />
            </ProtectedRoute>
          } />

          {/* Company Employee Routes */}
          <Route path="/company-employee/dashboard" element={
            <ProtectedRoute allowedRoles={['company_employee']}>
              <CompanyEmployeeDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-employee/apply-visa" element={
            <ProtectedRoute allowedRoles={['company_employee']}>
              <CompanyEmployeeDashboard />
            </ProtectedRoute>
          } />
          <Route path="/company-employee/my-applications" element={
            <ProtectedRoute allowedRoles={['company_employee']}>
              <CompanyEmployeeDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;

